import { canUseDOM } from '@/utils/dom-utils';
import { getQueryStrings } from '@/utils/uri-utils';
import { setCookie } from '@/utils/cookies-util';
import { hasConsent, waitForCookiebot, onCookiebotConsentChange } from '@/utils/cookiebot-utils';

const LINKSOURCE_COOKIE_NAME = 'LinkSource';
const LINKSOURCE_COOKIE_EXPIRE_DAYS = 1;
// LinkSource 属"记住营销来源"的归因 cookie，归类为 preferences
// 如需改为 marketing，将下一行改为 'marketing' 即可
const LINKSOURCE_CONSENT_TYPE = 'preferences';

// 当用户尚未同意 cookies 时，暂存当前 URL 的参数，等同意后补写
let pendingParams = null;
let consentListenerBound = false;

/**
 * 纯函数：根据 URL 参数解析出 LinkSource tag
 * 严格保持 PHP source() 方法的语义（白名单、映射顺序、chatgpt 覆盖优先级）
 * @param {{ src?: string, utm_source?: string, utm_medium?: string }} params
 * @returns {string} 匹配到的 tag，未命中返回空字符串
 */
export const parseLinkSourceTag = (params) => {
  const src = (params.src || '').toString().toLowerCase();
  const utm_source = (params.utm_source || '').toString().toLowerCase();
  const utm_medium = (params.utm_medium || '').toString().toLowerCase();

  let tag = '';

  const srcWhitelist = ['fromgenscriptbanner', 'fromgenscript', 'fromgenscriptsubpage'];
  const utmSourceWhitelist = [
    'google',
    'googlemob',
    'linkedin',
    'mkteloqua',
    'baidu',
    'bing',
    'hubspot',
    'hs_email',
  ];

  // 白名单命中才进入 switch 映射（与 PHP 外层大 if 一致）
  if (
    (src !== '' || utm_source !== '') &&
    (utmSourceWhitelist.includes(utm_source) || srcWhitelist.includes(src))
  ) {
    // 1) src 映射
    switch (src) {
      case 'fromgenscriptbanner':
      case 'fromgenscript':
      case 'fromgenscriptsubpage':
        tag = 'genscript';
        break;
      default:
        break;
    }

    // 2) utm_source 映射（与 PHP 两个 switch 串联一致，后者覆盖前者）
    switch (utm_source) {
      case 'google':
      case 'googlemob':
        tag = 'google';
        if (utm_medium === 'display') {
          tag = 'google_display';
        }
        break;
      case 'baidu':
        tag = 'baidu';
        if (utm_medium === 'cpc') {
          tag = 'baidu cpc';
        }
        break;
      case 'linkedin':
        tag = 'linkedin';
        break;
      case 'mkteloqua':
        tag = 'EDM';
        break;
      case 'bing':
        tag = 'Bing';
        break;
      case 'hubspot':
        tag = 'Hubspot';
        if (utm_medium === 'edm') {
          tag = 'EDM';
        }
        break;
      case 'hs_email':
        tag = 'EDM';
        break;
      default:
        break;
    }
  }

  // 3) chatgpt 专项（DEMAND-19440）：独立于白名单，只要 utm_source 包含 chatgpt 就覆盖
  // PHP 这里用 stripos，对大小写不敏感；此处 utm_source 已转小写
  if (utm_source !== '' && utm_source.includes('chatgpt')) {
    tag = 'chatgpt';
  }

  return tag;
};

/**
 * 写入 LinkSource cookie，与 PHP 一致：1 天有效期，path=/，domain=当前 hostname
 * @param {string} tag
 */
const writeLinkSourceCookie = (tag) => {
  if (!canUseDOM() || !tag) return;

  const domain = window.location.hostname;
  // 沿用 cookies-util 的 setCookie 处理 value 编码与过期时间
  setCookie(LINKSOURCE_COOKIE_NAME, tag, LINKSOURCE_COOKIE_EXPIRE_DAYS);

  // cookies-util 的 setCookie 未写 domain，这里再补写一次确保带 domain（与 PHP HTTP_HOST 一致）
  // 不覆盖 cookies-util 以免影响其它调用，通过直接设置 document.cookie 叠加
  try {
    const expiration = new Date();
    expiration.setDate(expiration.getDate() + LINKSOURCE_COOKIE_EXPIRE_DAYS);
    const safeValue = encodeURIComponent(tag);
    const sameSite = window.location.protocol === 'https:' ? 'None; Secure' : 'Lax';
    document.cookie =
      `${LINKSOURCE_COOKIE_NAME}=${safeValue};expires=${expiration.toUTCString()};` +
      `path=/;domain=${domain};SameSite=${sameSite};`;
  } catch (err) {
    console.error('Failed to write LinkSource cookie:', err);
  }
};

/**
 * 用暂存的参数解析并写 cookie（Cookiebot 同意后回调）
 */
const processPendingParams = () => {
  if (!pendingParams) return;
  const tag = parseLinkSourceTag(pendingParams);
  if (tag) writeLinkSourceCookie(tag);
  pendingParams = null;
};

/**
 * 确保只注册一次 Cookiebot 同意监听器
 */
const ensureConsentListener = () => {
  if (consentListenerBound) return;
  consentListenerBound = true;
  onCookiebotConsentChange(({ preferences, marketing }) => {
    // 与 LINKSOURCE_CONSENT_TYPE 的判断保持一致
    const consented = LINKSOURCE_CONSENT_TYPE === 'marketing' ? marketing : preferences;
    if (consented && pendingParams) {
      processPendingParams();
    }
  });
};

/**
 * 主入口：按规则匹配来源并写入 LinkSource cookie
 * 遵循 Cookiebot preferences 同意策略：未同意时暂存参数，同意后补写
 *
 * @param {Partial<{src: string, utm_source: string, utm_medium: string}>} [paramsOverride]
 *        可选参数覆盖。router.js afterEach 中可传入 to.query 派生值，更准确反映本次路由的 query；
 *        不传入时从当前 window.location.search 读取。
 *
 * 调用时机：
 *   - Layout.vue setup 中首次进入时调用一次
 *   - router.js 全局 afterEach 中每次路由变化调用一次
 */
export const captureLinkSource = async (paramsOverride) => {
  if (!canUseDOM()) return;

  let src, utm_source, utm_medium;
  if (paramsOverride) {
    src = paramsOverride.src;
    utm_source = paramsOverride.utm_source;
    utm_medium = paramsOverride.utm_medium;
  } else {
    // 从当前 URL 读取
    [src, utm_source, utm_medium] = getQueryStrings('src', 'utm_source', 'utm_medium');
  }
  if (!src && !utm_source) return; // 两个参数都空，直接跳过

  const params = {
    src: src ?? '',
    utm_source: utm_source ?? '',
    utm_medium: utm_medium ?? '',
  };

  const cookiebot = await waitForCookiebot();

  // 无 Cookiebot 集成或已同意 → 直接处理
  if (!cookiebot || hasConsent(LINKSOURCE_CONSENT_TYPE)) {
    const tag = parseLinkSourceTag(params);
    if (tag) writeLinkSourceCookie(tag);
    return;
  }

  // 尚未同意 → 暂存参数，等用户同意后再写 cookie
  pendingParams = params;
  ensureConsentListener();
};
